import React, { useState, useEffect, useRef } from "react";

const DEFAULT_STEPS = [
  { label: "Hands & Forearms", region: "arms",  tense: "Clench your fists tightly — hold...",           release: "Slowly release your hands — let go..." },
  { label: "Upper Arms",       region: "arms",  tense: "Flex your biceps — hold...",                    release: "Release your arms — feel them go heavy..." },
  { label: "Shoulders",        region: "torso", tense: "Raise your shoulders to your ears — hold...",   release: "Drop your shoulders — let them melt down..." },
  { label: "Chest & Stomach",  region: "torso", tense: "Tighten your stomach — hold...",                release: "Release your core — breathe softly..." },
  { label: "Legs & Thighs",    region: "legs",  tense: "Press your thighs together — hold...",          release: "Release your legs — let them fall open..." },
  { label: "Feet",             region: "legs",  tense: "Curl your toes tightly — hold...",              release: "Release your feet — spread your toes..." },
];

const TENSE_DURATION = 5;
const RELEASE_DURATION = 5;

// Dot positions as % of the figure container (matching the pictogram SVG)
const DOT_POSITIONS = [
  // Step 0: Hands — lower arms
  [{ top: "60%", left: "14%" }, { top: "60%", left: "86%" }],
  // Step 1: Upper Arms — mid arms
  [{ top: "38%", left: "14%" }, { top: "38%", left: "86%" }],
  // Step 2: Shoulders
  [{ top: "24%", left: "27%" }, { top: "24%", left: "73%" }],
  // Step 3: Chest & Stomach — center torso
  [{ top: "40%", left: "50%" }],
  // Step 4: Legs & Thighs
  [{ top: "72%", left: "34%" }, { top: "72%", left: "66%" }],
  // Step 5: Feet
  [{ top: "90%", left: "34%" }, { top: "90%", left: "66%" }],
];

// Which SVG regions have already been visited (for "done" state)
function getDoneRegions(steps, currentIdx) {
  const done = new Set();
  for (let i = 0; i < currentIdx; i++) done.add(steps[i].region);
  return done;
}

function getRegionClass(region, activeRegion, doneRegions, isRelease) {
  if (region === activeRegion) {
    return isRelease ? "pmr-region pmr-region-release" : "pmr-region pmr-region-active";
  }
  if (doneRegions.has(region)) return "pmr-region pmr-region-done";
  return "pmr-region";
}

function getDotClass(dotStepIdx, currentStep, isRelease) {
  if (dotStepIdx < currentStep) return "pmr-dot pmr-dot-done";
  if (dotStepIdx === currentStep) {
    return isRelease ? "pmr-dot pmr-dot-release" : "pmr-dot pmr-dot-active";
  }
  return "pmr-dot pmr-dot-idle";
}

export default function PMRTemplate({ config = {}, onProgress, onComplete }) {
  const steps = config.steps?.length ? config.steps : DEFAULT_STEPS;

  const [stepIndex, setStepIndex]   = useState(0);
  const [timeLeft, setTimeLeft]     = useState(TENSE_DURATION);
  const [isTense, setIsTense]       = useState(true);
  const [isRunning, setIsRunning]   = useState(false);
  const completedRef                = useRef(false);

  const done = stepIndex >= steps.length;
  const currentStep = steps[stepIndex] || steps[steps.length - 1];
  const activeRegion = done ? null : currentStep.region;
  const doneRegions = getDoneRegions(steps, stepIndex);

  // Report progress
  useEffect(() => {
    onProgress?.(
      stepIndex / steps.length,
      done ? "Complete" : `Step ${stepIndex + 1} of ${steps.length}`
    );
  }, [stepIndex]); // eslint-disable-line

  // Timer: tense → release → next step
  useEffect(() => {
    if (!isRunning || done) return;
    const interval = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          if (isTense) {
            // Switch to release phase
            setIsTense(false);
            return RELEASE_DURATION;
          } else {
            // Advance to next step
            const next = stepIndex + 1;
            setStepIndex(next);
            setIsTense(true);
            if (next >= steps.length) {
              setIsRunning(false);
              if (!completedRef.current) {
                completedRef.current = true;
                onComplete?.();
              }
            }
            return TENSE_DURATION;
          }
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isRunning, stepIndex, isTense, done]); // eslint-disable-line

  // Reset timer on step change
  useEffect(() => {
    setTimeLeft(TENSE_DURATION);
    setIsTense(true);
  }, [stepIndex]);

  const phaseColor = isTense ? "#CB5A3C" : "#8B7BE8";

  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-w-full">
      {/* Pictogram figure */}
      <div className="pmr-figure-area">
        <svg viewBox="0 0 200 420" fill="none" xmlns="http://www.w3.org/2000/svg" className="pmr-figure-svg">
          {/* Head */}
          <circle cx="100" cy="36" r="24"
            className={getRegionClass("head", activeRegion, doneRegions, !isTense)} />
          {/* Torso */}
          <rect x="62" y="74" width="76" height="168" rx="10"
            className={getRegionClass("torso", activeRegion, doneRegions, !isTense)} />
          {/* Left arm */}
          <rect x="22" y="82" width="30" height="148" rx="15"
            className={getRegionClass("arms", activeRegion, doneRegions, !isTense)} />
          {/* Right arm */}
          <rect x="148" y="82" width="30" height="148" rx="15"
            className={getRegionClass("arms", activeRegion, doneRegions, !isTense)} />
          {/* Left leg */}
          <rect x="62" y="248" width="32" height="140" rx="16"
            className={getRegionClass("legs", activeRegion, doneRegions, !isTense)} />
          {/* Right leg */}
          <rect x="106" y="248" width="32" height="140" rx="16"
            className={getRegionClass("legs", activeRegion, doneRegions, !isTense)} />
        </svg>

        {/* Dots */}
        {DOT_POSITIONS.map((positions, stepIdx) =>
          positions.map((pos, j) => (
            <div
              key={`${stepIdx}-${j}`}
              className={getDotClass(stepIdx, stepIndex, !isTense)}
              style={{ top: pos.top, left: pos.left }}
            />
          ))
        )}
      </div>

      {/* Instruction */}
      {!done && (
        <>
          <div className="tw-text-center">
            <span className="pmr-phase-badge" style={{
              color: phaseColor,
              background: isTense ? "rgba(203,90,60,0.1)" : "rgba(91,63,214,0.1)",
              borderColor: isTense ? "rgba(203,90,60,0.2)" : "rgba(91,63,214,0.2)",
            }}>
              {isTense ? "TENSE" : "RELEASE"}
            </span>
            <p className="tw-text-sm tw-font-light tw-max-w-[260px] tw-leading-relaxed tw-text-center tw-mt-2"
              style={{ color: "rgba(255,255,255,0.8)" }}>
              {isTense ? currentStep.tense : currentStep.release}
            </p>
          </div>

          {/* Timer */}
          <div
            className="tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-base tw-font-light tw-transition-all tw-duration-500"
            style={{
              width: 44, height: 44,
              color: "rgba(255,255,255,0.9)",
              border: `2px solid ${isRunning ? phaseColor : "rgba(255,255,255,0.15)"}`,
            }}
          >
            {timeLeft}
          </div>

          {/* Begin / Pause */}
          <button
            className="tw-px-7 tw-py-2 tw-rounded-3xl tw-text-white tw-font-medium tw-transition-all tw-duration-200 hover:tw-scale-105"
            style={{ background: "#5B3FD6", border: "none", cursor: "pointer" }}
            onClick={() => setIsRunning((r) => !r)}
          >
            {isRunning ? "Pause" : stepIndex === 0 && isTense ? "Begin" : "Resume"}
          </button>
        </>
      )}
    </div>
  );
}
